// What the Unable to capture action and dialog may offer on a loaded task, read through the same model rules the server records with (WBS-12336).
import { EvidenceEnforcement, EvidenceExceptionTag, findExceptableTaskEvidence, findMissingTaskEvidence, findTaskEvidenceExceptionLock, findTaskEvidenceStates, LabelType, resolveEvidenceLevel, resolveExplainedTypes, Task, TaskEvidenceKind, TaskEvidenceOutcome, toEvidenceExceptionType } from '@jms/model';

/** Only on missing Required (allow exceptions) evidence of an open task the user may add evidence to; it stays while that evidence is missing, so recording again replaces. */
export function canRecordUnableToCapture(task: Partial<Task> | null | undefined, kind: TaskEvidenceKind, canAddEvidence: boolean): boolean {

  return !!task && canAddEvidence && !findTaskEvidenceExceptionLock(task) && findExceptableTaskEvidence(task).includes(kind);
}

/** The dialog still opens on a closed task or without the right, to show the exception, but changes nothing. */
export function isEvidenceExceptionReadonly(task: Partial<Task> | null | undefined, canAddEvidence: boolean): boolean {

  return !canAddEvidence || !!findTaskEvidenceExceptionLock(task);
}

/** A retired tag, or one set up for none of the missing types, would be refused by the server. */
export function offerEvidenceExceptionReasons(task: Partial<Task>, tags: readonly EvidenceExceptionTag[] | null | undefined): EvidenceExceptionTag[] {

  return (tags ?? []).filter(tag => tag.type === LabelType.EvidenceException && tag.active !== false && resolveExplainedTypes(task, tag).length > 0);
}

/** The missing evidence a reason would explain if recorded now; other missing evidence stays Outstanding (D11). */
export function explainedEvidenceKinds(task: Partial<Task>, tag: EvidenceExceptionTag | null | undefined): TaskEvidenceKind[] {

  const types = resolveExplainedTypes(task, tag);

  return findExceptableTaskEvidence(task).filter(kind => types.includes(toEvidenceExceptionType(kind)));
}

/** No exception explains Required (no exceptions) evidence, so the dialog names what must still be captured. */
export function findUnexceptableTaskEvidence(task: Partial<Task>): TaskEvidenceKind[] {

  return findMissingTaskEvidence(task)
    .filter(shortfall => shortfall.level === EvidenceEnforcement.RequiredNoExceptions)
    .map(shortfall => shortfall.kind);
}

export type EvidenceExceptionAction = 'record' | 'edit' | 'view';

/** One collection's share of the heading action: record while Outstanding, edit (or view when locked) once the exception explains it, none once captured or when the level allows no exception. */
export function resolveEvidenceExceptionAction(task: Partial<Task> | null | undefined, kind: TaskEvidenceKind, canAddEvidence: boolean): EvidenceExceptionAction | undefined {

  if (!task || resolveEvidenceLevel(task, kind) !== EvidenceEnforcement.RequiredExceptionAllowed) return undefined;
  const state = findTaskEvidenceStates(task).find(evidence => evidence.kind === kind)?.state;
  if (state === TaskEvidenceOutcome.Exception) return isEvidenceExceptionReadonly(task, canAddEvidence) ? 'view' : 'edit';

  return state === TaskEvidenceOutcome.Outstanding && canRecordUnableToCapture(task, kind, canAddEvidence) ? 'record' : undefined;
}

/** The one action on the Evidence heading: a task holds one exception explaining every missing collection it applies to, so an existing one is edited (or viewed) before a new one is offered (WBS-12337 round 3). */
export function resolveTaskEvidenceExceptionAction(task: Partial<Task> | null | undefined, canAddEvidence: boolean): EvidenceExceptionAction | undefined {

  const actions = task ? findTaskEvidenceStates(task).map(evidence => resolveEvidenceExceptionAction(task, evidence.kind, canAddEvidence)) : [];

  return actions.find(action => action === 'edit' || action === 'view') ?? actions.find(action => action === 'record');
}

/** What the exception on the task explains now, for the read-only dialog. */
export function findExplainedTaskEvidence(task: Partial<Task>): TaskEvidenceKind[] {

  return findTaskEvidenceStates(task)
    .filter(evidence => evidence.state === TaskEvidenceOutcome.Exception)
    .map(evidence => evidence.kind);
}
